// src/components/ConsentCapture.jsx
//
// DPDP §6 layered consent UI — shown as the final step of patient registration.
// Clinical Architecture v5.7 §B5 four-layer consent model.
//
// Layer 1 (CORE_SERVICE) is mandatory — registration is blocked without it.
// Layer 4 (RESEARCH) is optional — has no service impact.
//
// Design system: Previu tokens only. No Tailwind. No hardcoded hex.
// Follows PatientLoginPage.jsx styling patterns.
//
// Gap Report — ConsentCapture.jsx
// ✅ Confirmed from requirements: 4-layer consent model, Layer 1 required, Layer 4 optional
// ⚠️ Assumptions: No Figma frame provided. Follows existing Previu card + input patterns.
// ❌ Missing Figma frames: All states (default, hover, error). Fallback: design-system-consistent.
// 🔗 Existing components reused: ToastContext (useToast), design tokens from index.css
// 🚫 Deviations from design tokens: None

import { useState } from 'react';

// Consent layer definitions — must match backend ConsentService.CORE_CONSENT_TEXT
const CONSENT_LAYERS = [
  {
    id: 'coreService',
    purpose: 'CORE_SERVICE',
    required: true,
    heading: 'Health data processing — required',
    badge: 'Required',
    text:
      'I consent to Previu Health storing my personal health information, including lab ' +
      'reports and health records, and using this information to generate health insights, ' +
      'triage results, and care recommendations as part of the Previu preventive health ' +
      'programme. I understand that my data is processed securely and that I may withdraw ' +
      'this consent at any time by contacting Previu Health.',
    note: 'This consent is required to use Previu. Without it, your health data cannot be processed.',
  },
  {
    id: 'dataSource',
    purpose: 'DATA_SOURCE',
    required: false,
    heading: 'Lab result sharing — optional',
    badge: 'Optional',
    text:
      'I consent to Previu Health receiving lab results directly from partner laboratories ' +
      '(e.g. Thyrocare) on my behalf, so that my health records are updated automatically ' +
      'when tests are ordered through the platform.',
    note: 'You can still upload lab reports manually if you prefer.',
  },
  {
    id: 'research',
    purpose: 'RESEARCH',
    required: false,
    heading: 'Anonymised health research — optional',
    badge: 'Optional',
    text:
      'I consent to my anonymised health data being included in population health research ' +
      'aimed at improving preventive care outcomes for South Asian communities. My personal ' +
      'identity will never be included. This consent has no impact on the service I receive.',
    note: 'Your identity is never shared. This helps improve health outcomes for communities like yours.',
  },
];

const DPDP_NOTE =
  'Your consent is governed by the Digital Personal Data Protection Act 2023 (DPDP). ' +
  'You may withdraw any consent at any time from your account settings.';

export default function ConsentCapture({ onConsentComplete }) {
  const [checked, setChecked] = useState({
    coreService: false,
    dataSource:  false,
    research:    false,
  });
  const [showError, setShowError] = useState(false);
  const [expanded, setExpanded]   = useState({ coreService: true, dataSource: false, research: false });

  function toggle(id) {
    setChecked(prev => ({ ...prev, [id]: !prev[id] }));
    if (id === 'coreService') setShowError(false);
  }

  function toggleExpand(id) {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  }

  function handleContinue() {
    if (!checked.coreService) {
      setShowError(true);
      return;
    }
    // Pass consent decisions to parent (RegisterPage)
    onConsentComplete({
      consentGiven: true,
      layers: CONSENT_LAYERS.map(l => ({
        purpose: l.purpose,
        given:   checked[l.id],
      })),
    });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, letterSpacing: '0.1em',
          textTransform: 'uppercase', color: 'var(--brand-accent)',
          fontFamily: 'var(--font-body)', marginBottom: 6,
        }}>
          Your data, your choice
        </div>
        <h2 style={{
          fontSize: 22, fontWeight: 700, color: 'var(--text-primary)',
          fontFamily: 'var(--font-display)', margin: 0, lineHeight: 1.3,
        }}>
          Before we continue
        </h2>
        <p style={{
          marginTop: 8, fontSize: 14, color: 'var(--text-secondary)',
          fontFamily: 'var(--font-body)', lineHeight: 1.7,
        }}>
          Previu handles sensitive health information. Please read and choose your
          preferences below before we create your account.
        </p>
      </div>

      {/* Consent layers */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
        {CONSENT_LAYERS.map(layer => {
          const isChecked  = checked[layer.id];
          const isExpanded = expanded[layer.id];
          const isRequired = layer.required;
          const hasError   = isRequired && showError && !isChecked;

          return (
            <div
              key={layer.id}
              style={{
                borderRadius: 10,
                border: `1.5px solid ${hasError
                  ? 'var(--red)'
                  : isChecked
                    ? 'var(--green)'
                    : 'var(--border)'}`,
                background: isChecked
                  ? 'var(--green-bg)'
                  : hasError
                    ? 'var(--red-bg)'
                    : 'var(--bg-surface)',
                transition: 'border-color 0.2s, background 0.2s',
                overflow: 'hidden',
              }}
            >
              {/* Layer header row */}
              <div
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 12,
                  padding: '14px 16px', cursor: 'pointer',
                }}
                onClick={() => toggleExpand(layer.id)}
                role="button"
                tabIndex={0}
                onKeyDown={e => e.key === 'Enter' && toggleExpand(layer.id)}
                aria-expanded={isExpanded}
              >
                {/* Checkbox — separate click target */}
                <div
                  onClick={e => { e.stopPropagation(); toggle(layer.id); }}
                  role="checkbox"
                  aria-checked={isChecked}
                  tabIndex={0}
                  onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.stopPropagation(); toggle(layer.id); }}}
                  aria-label={`${layer.heading} consent`}
                  style={{
                    width: 20, height: 20, borderRadius: 5, flexShrink: 0, marginTop: 1,
                    border: `2px solid ${isChecked ? 'var(--green)' : 'var(--border)'}`,
                    background: isChecked ? 'var(--green)' : 'var(--bg-surface)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                >
                  {isChecked && (
                    <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                      <path d="M1 4.5L4 7.5L10 1.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>

                {/* Text */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{
                      fontSize: 14, fontWeight: 600, color: 'var(--text-primary)',
                      fontFamily: 'var(--font-body)',
                    }}>
                      {layer.heading}
                    </span>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '2px 7px',
                      borderRadius: 20, letterSpacing: '0.05em',
                      background: isRequired ? 'rgba(192,57,43,0.1)' : 'var(--bg-subtle)',
                      color: isRequired ? 'var(--red)' : 'var(--text-muted)',
                      fontFamily: 'var(--font-body)',
                    }}>
                      {layer.badge}
                    </span>
                  </div>
                </div>

                {/* Expand chevron */}
                <svg
                  width="16" height="16" viewBox="0 0 16 16" fill="none"
                  style={{
                    flexShrink: 0, transition: 'transform 0.2s',
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    color: 'var(--text-muted)',
                  }}
                >
                  <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>

              {/* Expanded consent text */}
              {isExpanded && (
                <div style={{
                  padding: '0 16px 14px 48px',
                  borderTop: '1px solid var(--border)',
                  marginTop: 0,
                }}>
                  <p style={{
                    fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7,
                    fontFamily: 'var(--font-body)', margin: '12px 0 8px',
                  }}>
                    {layer.text}
                  </p>
                  <p style={{
                    fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5,
                    fontFamily: 'var(--font-body)', margin: 0,
                    borderLeft: '2px solid var(--border)',
                    paddingLeft: 10,
                  }}>
                    {layer.note}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Error message */}
      {showError && (
        <div style={{
          fontSize: 13, color: 'var(--red)', marginBottom: 16,
          padding: '10px 14px', borderRadius: 8,
          background: 'var(--red-bg)',
          fontFamily: 'var(--font-body)', lineHeight: 1.5,
        }}
          role="alert"
        >
          Please read and accept the health data processing consent (required) to continue.
        </div>
      )}

      {/* DPDP footer note */}
      <p style={{
        fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.6,
        fontFamily: 'var(--font-body)', marginBottom: 20,
      }}>
        {DPDP_NOTE}
      </p>

      {/* CTA */}
      <button
        type="button"
        onClick={handleContinue}
        style={{
          width: '100%', border: 'none',
          borderRadius: 10, padding: '13px 18px',
          fontSize: 15, fontWeight: 600,
          color: 'var(--text-inverse)',
          background: checked.coreService ? 'var(--brand-dark)' : 'var(--text-muted)',
          cursor: checked.coreService ? 'pointer' : 'not-allowed',
          fontFamily: 'var(--font-body)',
          transition: 'background 0.2s',
        }}
        aria-disabled={!checked.coreService}
      >
        Continue to registration
      </button>
    </div>
  );
}
